import { FocusEventHandler, useEffect, useMemo, useState } from 'react';
import { isDefined, Nullable, Validation } from '@e2m-customer-portal/shared';

import { useValidationContext } from '../use-validation-context/use-validation-context';

interface UseInputValidationResult {
  /**
   * Should be set as the blur handler for inputs. Used to show validation errors when the user leaves the field.
   */
  handleBlur: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;

  /**
   * Whether there was a validation error. Will only be true on error if the input is validating, for example by
   * setting the `validating` property of a containing `ValidationContainer` to `on` or using the `setValidating`
   * property.
   */
  error: boolean;

  /**
   * If there was a validation error, contains the relevant error message, which can be displayed to the user.
   */
  message: Nullable<string>;

  /**
   * Whether there was a validation error. This will reflect the current validation status whether or not the
   * input is currently showing errors.
   */
  isValid: boolean;

  /**
   * Used to manually set whether to activate or deactivate validation. Usually not necessary, as most cases are
   * handled by the blur handler.
   * @param validate
   */
  setValidating: (validate: boolean) => void;
}

/**
 * A hook that implements the functionality to validate an input and notify the validation context. The returned
 * values should be applied as props to the underlying input.
 *
 * @param name
 * @param rules
 * @param value
 * @param onBlur
 */
export function useInputValidation<T>(
  name: string,
  rules: Nullable<Validation<T>[]>,
  value: Nullable<T>,
  onBlur?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>,
): UseInputValidationResult {
  const context = useValidationContext();
  const [validating, setValidating] = useState(context.mode === 'on');

  const message: Nullable<string> = (rules ?? []).find(({ validate }) => !validate(value))?.message;

  if (validating && context.mode === 'off') {
    setValidating(false);
  }

  if (!validating && context.mode === 'on') {
    setValidating(true);
  }

  useEffect(() => {
    context.onChange(name, message == null);
  });

  return useMemo(
    () => ({
      handleBlur: (event) => {
        onBlur?.(event);
        if (context.mode !== 'blur') return;
        if (validating) return;
        setValidating(true);
      },
      error: validating && isDefined(message),
      message: validating ? message : undefined,
      isValid: message == null,
      setValidating,
    }),
    [context.mode, validating, message, onBlur],
  );
}
